// Mapeamento de imagens organizadas por ID do imóvel
// As imagens agora estão em public/1/, public/2/, etc.
import { getPropertyImageUrl } from '@/config/api';

// Função utilitária para obter a URL da imagem baseada no ID do imóvel
export const getImageUrl = (imageName: string, propertyId?: number, useThumbnail: boolean = false): string => {
  if (propertyId) {
    if (useThumbnail && imageName && !imageName.startsWith('thumb/') && !imageName.startsWith('http')) {
      const thumbPath = `thumb/${imageName}`;
      const timestamp = Date.now();
      return getPropertyImageUrl(propertyId, thumbPath, timestamp);
    }
    const timestamp = Date.now();
    return getPropertyImageUrl(propertyId, imageName, timestamp);
  }
  return `/placeholder.svg`;
};

// Função utilitária para obter a primeira imagem de um array
export const getFirstImageUrl = (images: string[] | string | null | undefined, propertyId?: number): string => {
  if (!images) return "/placeholder.svg";
  
  if (Array.isArray(images) && images.length > 0) {
    return getImageUrl(images[0], propertyId);
  }
  
  if (typeof images === 'string') {
    return getImageUrl(images, propertyId);
  }
  
  return "/placeholder.svg";
};

// Função utilitária para obter a imagem de capa de uma propriedade
// photos: array opcional com as fotos do imóvel (para seleção aleatória quando não há cover_image)
// useThumbnail: true → tenta /thumb/ no backend (mais leve em listagens)
export const getCoverImageUrl = (property: any, useThumbnail: boolean = false, photos?: Array<{ filename: string }>): string => {
  if (!property) {
    return "/placeholder.svg";
  }
  
  const propertyId = property.property_id || property.id;
  
  if (!propertyId) {
    return "/placeholder.svg";
  }
  
  if (property.cover_image && property.cover_image.trim() !== '') {
    if (property.cover_image.startsWith('http://') || property.cover_image.startsWith('https://')) {
      return property.cover_image;
    }
    return getImageUrl(property.cover_image, propertyId, useThumbnail);
  }
  
  if (photos && photos.length > 0) {
    const randomIndex = propertyId % photos.length;
    const randomPhoto = photos[randomIndex];
    return getImageUrl(randomPhoto.filename, propertyId, useThumbnail);
  }
  
  if (property.images) {
    try {
      if (Array.isArray(property.images)) {
        if (property.images.length > 0) {
          return getImageUrl(property.images[0], propertyId, useThumbnail);
        }
      } else if (typeof property.images === 'string') {
        const imagesArray = JSON.parse(property.images);
        if (Array.isArray(imagesArray) && imagesArray.length > 0) {
          return getImageUrl(imagesArray[0], propertyId, useThumbnail);
        }
      }
    } catch {
      /* ignore */
    }
  }
  
  const fallbackImages: Record<number, string> = {
    1: 'apartment-1.jpg',
    2: 'house-1.jpg', 
    3: 'penthouse-1.jpg',
    4: 'cottage-1.jpg',
    5: 'resort-infrastructure.jpg',
    6: 'city-1.jpg'
  };
  
  const fallbackImage = fallbackImages[propertyId];
  if (fallbackImage) {
    return getImageUrl(fallbackImage, propertyId, useThumbnail);
  }
  
  return "/placeholder.svg";
};

// Função para obter todas as imagens de um imóvel
export const getPropertyImages = (propertyId: number): string[] => {
  const imageMappings: { [key: number]: string[] } = {
    1: ['apartment-1.jpg', 'hero-muro-alto-1.jpg', 'hero-muro-alto-2.jpg', 'hero-muro-alto-3.jpg'],
    2: ['house-1.jpg', 'hero-muro-alto-4.jpg', 'hero-muro-alto-5.jpg'],
    3: ['penthouse-1.jpg', 'hero-beach.jpg', 'piscinas-naturais.jpg'],
    4: ['cottage-1.jpg', 'porto-galinhas-town.jpg', 'resort-1.jpg'],
    5: ['resort-infrastructure.jpg', 'city-1.jpg', 'partner-1.jpg']
  };
  
  return imageMappings[propertyId] || [];
};
